import {signedDownloadCache} from './services/SignedDownloadCache';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import {Box, CircularProgress, Container, Grid} from '@mui/material';
import Header from './components/header/Header';
import NavBar from './components/navBar/NavBar';
import Routers from './components/routes/Router';
import { IUser } from './models/IUser';
import Login from './components/login/Login';
import PasswordRecoveryPage from './components/login/PasswordRecoveryPage';
import { useStyles } from './appStyles';
import {uiTokens} from './components/common/layout/designTokens';
import {Navigate, useLocation, useNavigate} from 'react-router-dom';
import {Alert, Snackbar} from '@mui/material';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {accountDeletionAPI} from './services/AccountDeletionServices';
import {AccountDeletionNotice} from './components/settings/AccountDeletionPanel';
import {resetAccountDeletion} from './models/AccountDeletion';
import {useAccountDeletionRefresh} from './hooks/useAccountDeletionRefresh';
import RegistrationPage from './components/registration/RegistrationPage';
import ConfirmEmailPage from './components/registration/ConfirmEmailPage';
import InviteRegistrationPage from './components/registration/InviteRegistrationPage';
import {authAPI} from './services/AuthServices';
import {userAPI} from './services/UserServices';
import {baseAPI} from './services/BaseAPI';
import {useAppDispatch} from './hooks/redux';
import {RequireAuthenticated, RequirePermission, isSafeAuthenticatedReturnPath} from './components/auth/routeGuards';
import {useSelectNumericInputOnFocus} from './hooks/useSelectNumericInputOnFocus';
import {useProfileLanguage} from './hooks/useProfileLanguage';

const AdminPanel = lazy(() => import(/* webpackChunkName: "admin-panel" */ './components/admin/AdminPanel'));

function App() {
    useSelectNumericInputOnFocus();
    const location = useLocation();
    const navigate = useNavigate();
    const {t} = useAppTranslation();
    const [deletionExpired, setDeletionExpired] = useState(false);
    const [accountRestored, setAccountRestored] = useState(false);
    const expiredHandled = useRef(false);
    const dispatch = useAppDispatch();
    const headerSizeInRem = 3;
    const [user, setUser] = useState<IUser | undefined>(undefined);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);
    const [fs, setFS] = useState(16);
    const ref = useRef(null);

    //style
    const { appWrapper, appWrapperContent, navBarWrapper, contentWrapper } = useStyles({ windowHeight, headerSizeInRem, fs });

    const {
        data: auth,
        error: authQueryError,
        isLoading: authIsLoading,
        refetch: refetchAuth,
    } = authAPI.useGetAuthContextQuery();
    const isAuthUnauthorized = Boolean(authQueryError && typeof authQueryError === 'object' && 'status' in authQueryError && authQueryError.status === 401);
    const authError = isAuthUnauthorized ? undefined : authQueryError;
    const isAuthenticated = auth?.isAuthenticated === true && !isAuthUnauthorized;
    const {data: userData, isLoading: userIsLoading} = userAPI.useGetUserDataQuery(undefined, {skip: !isAuthenticated, refetchOnMountOrArgChange: true});
    const profileLanguageReady = useProfileLanguage(isAuthenticated, userData?.language);
    const {refetch: refetchDeletion} = accountDeletionAPI.useGetAccountDeletionQuery(undefined, {skip: !isAuthenticated});

    useAccountDeletionRefresh(isAuthenticated, refetchAuth, refetchDeletion);

    // check the window height and font size
    useEffect(() => {
        function handleResize() {
            setWindowHeight(window.innerHeight);
        }
        window.addEventListener('resize', handleResize);

        const fontSize = ref.current ? window.getComputedStyle(ref.current).fontSize : '16px';
        const fontSizeValue = typeof fontSize === 'string' ? fontSize.match(/\d+/) : null;
        const number = fontSizeValue ? parseInt(fontSizeValue[0]) : 16;

        setFS(number);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const restored = () => setAccountRestored(true);
        window.addEventListener('bakery:account-restored', restored);
        return () => window.removeEventListener('bakery:account-restored', restored);
    }, []);

    useEffect(() => {
        if (isAuthenticated) {setDeletionExpired(false); expiredHandled.current = false;}
    }, [isAuthenticated]);

    useEffect(() => {
        const handleUnauthorized = (event: Event) => {
            const due = (event as CustomEvent).detail?.code === 'account.deletion_due';
            if (due) {
                setDeletionExpired(true);
                navigate('/login', {replace: true});
                if (expiredHandled.current) return;
                expiredHandled.current = true;
            }
            signedDownloadCache.clear();
            resetAccountDeletion();
            setUser(undefined);
            dispatch(baseAPI.util.resetApiState());
        };
        window.addEventListener('bakery:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('bakery:unauthorized', handleUnauthorized);
    }, [dispatch, navigate]);

    useEffect(() => {
        setUser(isAuthenticated ? userData : undefined);
    }, [isAuthenticated, userData]);

    const isLanding = location.pathname === '/';
    const isLogin = location.pathname === '/login';
    const isRegistration = location.pathname === '/register' || location.pathname === '/register/invite';
    const isApplication = location.pathname === '/app' || location.pathname.startsWith('/app/');
    const isAdmin = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
    const isConfirmEmail = location.pathname === '/register/confirm-email';
    const isPasswordRecovery = location.pathname === '/forgot-password' || location.pathname === '/reset-password';
    const isPublicPage = isLanding || isLogin || isRegistration || isConfirmEmail || isPasswordRecovery;

    const legacyApplicationPaths = ['/orders', '/items', '/recipes', '/customers', '/finances', '/settings'];
    const legacyPath = legacyApplicationPaths.find(path => location.pathname === path || location.pathname.startsWith(`${path}/`));
    const loginReturnPath = (location.state as {from?: string} | null)?.from;
    const safeLoginReturnPath = isSafeAuthenticatedReturnPath(loginReturnPath) ? loginReturnPath : '/app';
    const authIsResolving = authIsLoading || (isAuthenticated && (userIsLoading || !profileLanguageReady));

    const loading = <Box sx={{minHeight: '70vh', display: 'grid', placeItems: 'center'}}><CircularProgress/></Box>;
    const handleLoggedOut = () => {
        signedDownloadCache.clear();
        resetAccountDeletion();
        setUser(undefined);
        dispatch(baseAPI.util.resetApiState());
    };

    let page: React.ReactNode;
    if (legacyPath) {
        page = <Navigate replace to={`/app${location.pathname}${location.search}${location.hash}`}/>;
    } else if (isLanding) {
        page = authIsResolving ? loading : <Navigate replace to={isAuthenticated ? "/app" : "/login"}/>;
    } else if (isPasswordRecovery) {
        page = <PasswordRecoveryPage key={location.pathname} mode={location.pathname === '/reset-password' ? 'reset' : 'forgot'} onPasswordReset={handleLoggedOut}/>;
    } else if (isConfirmEmail) {
        page = <ConfirmEmailPage/>;
    } else if (isRegistration) {
        page = authIsResolving ? loading : isAuthenticated ? <Navigate replace to="/app"/> : location.pathname === '/register/invite' ? <InviteRegistrationPage/> : <RegistrationPage/>;
    } else if (isLogin) {
        page = authIsResolving ? loading : isAuthenticated ? <Navigate replace to={safeLoginReturnPath}/> : <Login/>;
    } else if (isApplication) {
        page = <RequireAuthenticated auth={isAuthUnauthorized ? undefined : auth} isLoading={authIsResolving} error={authError} retry={refetchAuth}>
            <Container
                maxWidth={false}
                disableGutters
                sx={{width: uiTokens.page.containerWidth, maxWidth: uiTokens.page.containerMaxWidth, mx: 'auto'}}
            >
                <Box sx={navBarWrapper}><Grid item md={12}><NavBar/></Grid></Box>
                <Grid item md={12}><Box sx={appWrapperContent} id="contentPageContainer"><Routers/></Box></Grid>
            </Container>
        </RequireAuthenticated>;
    } else if (isAdmin) {
        page = <RequirePermission auth={isAuthUnauthorized ? undefined : auth} isLoading={authIsResolving} error={authError} retry={refetchAuth} permission="admin.access">
            <Suspense fallback={loading}><AdminPanel/></Suspense>
        </RequirePermission>;
    } else {
        page = <Navigate replace to="/"/>;
    }

    return (
        <Box sx={appWrapper} ref={ref}>
            <Grid item md={12} sx={{display: isPublicPage ? 'none' : 'block'}}>
                {!authIsResolving && <Header user={user} canOpenAdmin={Boolean(auth?.permissions.includes('admin.access'))} onLoggedOut={handleLoggedOut}/>}
            </Grid>
            {deletionExpired && isLogin && <Alert severity="warning">{t('settings:accountDeletion.expired')}</Alert>}
            <Box sx={isPublicPage ? undefined : contentWrapper}>
                {isAuthenticated && !isPublicPage && <AccountDeletionNotice/>}
                {page}
            </Box>
            <Snackbar open={accountRestored} autoHideDuration={6000} onClose={() => setAccountRestored(false)}>
                <Alert severity="success" onClose={() => setAccountRestored(false)}>{t('settings:accountDeletion.restored')}</Alert>
            </Snackbar>
        </Box>
    );
}

export default App;
